import { DEFAULT_QUERY_LANGUAGE } from "./queryLanguage";
import { detectFeatureRequests } from "./features";
import { detectRvType } from "./rvTypes";
import type {
  ConditionFilter,
  LengthFilter,
  ParsedQuery,
  PriceFilter,
  QueryLanguageConfig,
  TemporalContext,
  WeightFilter,
} from "./types";

/** Words that describe the trip/situation rather than the product itself. */
const CONTEXT_WORDS = new Set([
  "trip", "trips", "vacation", "camping", "camp", "weekend", "family",
  "kids", "kid", "dog", "dogs", "pet", "pets", "travel", "road", "holiday",
  "honeymoon", "tailgating", "fulltime", "seasonal", "budget", "beach",
  "mountains", "lake", "park", "campground", "boondocking", "winter",
  "summer", "spring", "fall", "retirement", "couple", "grandkids",
]);

const SMART_QUOTES = /[\u2018\u2019\u201B\u2032]/g;
const SMART_DQUOTES = /[\u201C\u201D\u201F\u2033]/g;

export function normalizeText(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(SMART_QUOTES, "'")
    .replace(SMART_DQUOTES, '"')
    .toLowerCase()
    .replace(/[^a-z0-9$'\-./,\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

let cachedPatterns: { key: string; regexes: RegExp[] } | null = null;
function protectedRegexes(cfg: QueryLanguageConfig): RegExp[] {
  const key = cfg.protectedPatterns.join("|") + `#${cfg.version}`;
  if (cachedPatterns?.key === key) return cachedPatterns.regexes;
  const regexes = cfg.protectedPatterns.map((p) => new RegExp(p, "i"));
  cachedPatterns = { key, regexes };
  return regexes;
}

/** True when a token is an identifier that must survive untouched. */
export function isProtectedToken(token: string, cfg: QueryLanguageConfig = DEFAULT_QUERY_LANGUAGE): boolean {
  if (!token) return false;
  if (/\d/.test(token) && /^[a-z0-9$.,\-/]+$/.test(token)) {
    if (protectedRegexes(cfg).some((re) => re.test(token))) return true;
    // measurement forms written with a space collapse into "50 amp" handled elsewhere
    if (/^\d+(\.\d+)?$/.test(token) && token.length >= 3) return true;
  }
  return false;
}

/** Safe stemming: possessive → plural → ing → ed, with configurable exceptions. */
export function stemToken(token: string, cfg: QueryLanguageConfig = DEFAULT_QUERY_LANGUAGE): string {
  if (!token) return token;
  const exceptions = new Set(cfg.stemmingExceptions.map((w) => w.toLowerCase()));
  let w = token.toLowerCase();

  // possessive: rv's -> rv, boss' -> boss
  w = w.replace(/'s$/, "").replace(/s'$/, "s");
  if (exceptions.has(w)) return w;
  if (isProtectedToken(w, cfg)) return w;
  if (w.length <= 3) return w;

  // plurals
  if (w.endsWith("ies") && w.length > 4) {
    const cand = w.slice(0, -3) + "y";
    if (!exceptions.has(cand)) return cand;
  }
  if (/(ches|shes|sses|xes|zes)$/.test(w)) {
    const cand = w.slice(0, -2);
    if (!exceptions.has(cand)) return cand;
  }
  if (w.endsWith("s") && !/(ss|us|is|as|os)$/.test(w)) {
    const cand = w.slice(0, -1);
    if (cand.length >= 3 && !exceptions.has(cand)) w = cand;
  }
  if (exceptions.has(w)) return w;

  // -ing (lifting -> lift, hiking -> hike)
  if (w.endsWith("ing") && w.length > 5) {
    let base = w.slice(0, -3);
    if (base.length >= 3 && base[base.length - 1] === base[base.length - 2]) base = base.slice(0, -1);
    if (base.length >= 3 && !exceptions.has(base)) return base;
  }
  // -ed
  if (w.endsWith("ed") && w.length > 4) {
    const base = w.slice(0, -2);
    if (base.length >= 3 && !exceptions.has(base)) return base;
  }
  return w;
}

function stripPhrases(text: string, phrases: string[]): { text: string; removed: string[] } {
  let out = ` ${text} `;
  const removed: string[] = [];
  const sorted = [...phrases].sort((a, b) => b.length - a.length);
  for (const p of sorted) {
    const needle = ` ${p.toLowerCase()} `;
    if (out.includes(needle)) {
      removed.push(p);
      out = out.split(needle).join(" ");
    }
  }
  return { text: out.replace(/\s+/g, " ").trim(), removed };
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,]/g, "");
  const k = /k$/i.test(cleaned);
  const n = parseFloat(cleaned.replace(/k$/i, ""));
  if (!Number.isFinite(n)) return null;
  return k ? n * 1000 : n;
}

function detectPrice(text: string, cfg: QueryLanguageConfig): { filter: PriceFilter | null; text: string } {
  const amount = "\\$?([\\d][\\d,.]*k?)";
  const currency = "(?:\\s*(?:dollars?|usd|bucks))?";
  // range first
  const rangeRe = new RegExp(`between\\s+${amount}${currency}\\s+(?:and|to|-)\\s+${amount}${currency}`, "i");
  const rangeMatch = text.match(rangeRe);
  if (rangeMatch) {
    const min = parseAmount(rangeMatch[1] ?? "");
    const max = parseAmount(rangeMatch[2] ?? "");
    return {
      filter: { phrase: rangeMatch[0], type: "range", ...(min != null ? { min } : {}), ...(max != null ? { max } : {}) },
      text: text.replace(rangeMatch[0], " "),
    };
  }
  const keys = Object.keys(cfg.priceExpressions)
    .filter((k) => k !== "between")
    .sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const re = new RegExp(
      `${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:\\s+than)?\\s+${amount}${currency}`,
      "i",
    );
    const m = text.match(re);
    if (!m) continue;
    const value = parseAmount(m[1] ?? "");
    if (value == null) continue;
    const kind = cfg.priceExpressions[key] === "min" ? "min" : "max";
    return {
      filter: { phrase: m[0], type: kind, ...(kind === "max" ? { max: value } : { min: value }) },
      text: text.replace(m[0], " "),
    };
  }

  // "around $30,000" / "about 30k" -> +/-10% window
  const approx = text.match(new RegExp(`(?:around|about|near|approximately|roughly)\\s+${amount}${currency}`, "i"));
  if (approx) {
    const value = parseAmount(approx[1] ?? "");
    if (value != null && value >= 100) {
      return {
        filter: {
          phrase: approx[0],
          type: "exact",
          min: Math.round(value * 0.9),
          max: Math.round(value * 1.1),
        },
        text: text.replace(approx[0], " "),
      };
    }
  }

  // bare currency amount ("$30,000", "20000 dollars") -> +/-10% window
  const bare = text.match(new RegExp(`(?:\\$([\\d][\\d,.]*k?)|\\b([\\d][\\d,.]*k?)\\s*(?:dollars?|usd|bucks))`, "i"));
  if (bare) {
    const value = parseAmount(bare[1] ?? bare[2] ?? "");
    if (value != null && value >= 100) {
      return {
        filter: {
          phrase: bare[0],
          type: "exact",
          min: Math.round(value * 0.9),
          max: Math.round(value * 1.1),
        },
        text: text.replace(bare[0], " "),
      };
    }
  }
  return { filter: null, text };
}

function detectTemporal(text: string, cfg: QueryLanguageConfig): { ctx: TemporalContext | null; text: string } {
  const phrases = Object.keys(cfg.timeExpressions).sort((a, b) => b.length - a.length);
  for (const p of phrases) {
    if (` ${text} `.includes(` ${p} `)) {
      return {
        ctx: { phrase: p, type: cfg.timeExpressions[p] ?? "recent" },
        text: ` ${text} `.split(` ${p} `).join(" ").replace(/\s+/g, " ").trim(),
      };
    }
  }
  return { ctx: null, text };
}

const WEIGHT_UNIT = "(?:lbs?|pounds?|#)";
const LENGTH_UNIT = "(?:ft|foot|feet|')";

/** Plausible RV overall length in feet. */
function plausibleFeet(n: number | null): boolean {
  return n != null && Number.isFinite(n) && n >= 8 && n <= 70;
}

/**
 * Overall-length extraction: "25ft", "25 ft", "25'", "25 feet",
 * "under 30 feet", "at least 28 ft", "between 24 and 28 ft".
 * Runs BEFORE weight/price so the number is never read as dollars or pounds.
 */
function detectLength(text: string, cfg: QueryLanguageConfig): { filter: LengthFilter | null; text: string } {
  const amount = "(\\d{1,2}(?:\\.\\d+)?)";
  const rangeRe = new RegExp(
    `between\\s+${amount}\\s*${LENGTH_UNIT}?\\s+(?:and|to|-)\\s+${amount}\\s*${LENGTH_UNIT}`,
    "i",
  );
  const rangeMatch = text.match(rangeRe);
  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1] ?? "");
    const max = parseFloat(rangeMatch[2] ?? "");
    if (plausibleFeet(min) && plausibleFeet(max)) {
      return {
        filter: { phrase: rangeMatch[0], type: "range", min, max },
        text: text.replace(rangeMatch[0], " "),
      };
    }
  }

  const keys = Object.keys(cfg.priceExpressions)
    .filter((k) => k !== "between")
    .sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const re = new RegExp(
      `${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:\\s+than)?\\s+${amount}\\s*${LENGTH_UNIT}`,
      "i",
    );
    const m = text.match(re);
    if (!m) continue;
    const value = parseFloat(m[1] ?? "");
    if (!plausibleFeet(value)) continue;
    const kind = cfg.priceExpressions[key] === "min" ? "min" : "max";
    return {
      filter: { phrase: m[0], type: kind, ...(kind === "max" ? { max: value } : { min: value }) },
      text: text.replace(m[0], " "),
    };
  }

  // bare length ("25ft travel trailer", "looking for a 25' unit") -> ±2 ft window
  const bare = text.match(new RegExp(`\\b${amount}\\s*${LENGTH_UNIT}(?![a-z0-9])`, "i"));
  if (bare) {
    const value = parseFloat(bare[1] ?? "");
    if (plausibleFeet(value)) {
      return {
        filter: { phrase: bare[0], type: "exact", min: value - 2, max: value + 2 },
        text: text.replace(bare[0], " "),
      };
    }
  }
  return { filter: null, text };
}

/**
 * Condition ("new" vs "used") extraction. The matched word is removed from the
 * text so it never becomes a required search token — inventory titles rarely
 * contain the words "new" or "used".
 */
function detectCondition(text: string): { filter: ConditionFilter | null; text: string } {
  const patterns: Array<{ re: RegExp; value: "new" | "used" }> = [
    { re: /\b(?:pre[\s-]?owned|preowned|second[\s-]?hand|used)\b/i, value: "used" },
    { re: /\b(?:brand[\s-]?new|new)\b/i, value: "new" },
  ];
  for (const { re, value } of patterns) {
    const m = text.match(re);
    if (!m) continue;
    return {
      filter: { phrase: m[0], value },
      text: text.replace(re, " ").replace(/\s+/g, " ").trim(),
    };
  }
  return { filter: null, text };
}

/**
 * Dry-weight extraction. Runs BEFORE price detection so "under 5000 lbs" is
 * read as a weight limit instead of a $5,000 price cap.
 */
function detectWeight(text: string, cfg: QueryLanguageConfig): { filter: WeightFilter | null; text: string } {
  const amount = "(\\d[\\d,.]*k?)";
  // "between 4000 and 6000 lbs"
  const rangeRe = new RegExp(`between\\s+${amount}\\s*${WEIGHT_UNIT}?\\s+(?:and|to|-)\\s+${amount}\\s*${WEIGHT_UNIT}`, "i");
  const rangeMatch = text.match(rangeRe);
  if (rangeMatch) {
    const min = parseAmount(rangeMatch[1] ?? "");
    const max = parseAmount(rangeMatch[2] ?? "");
    return {
      filter: {
        phrase: rangeMatch[0],
        type: "range",
        ...(min != null ? { min } : {}),
        ...(max != null ? { max } : {}),
      },
      text: text.replace(rangeMatch[0], " "),
    };
  }

  const keys = Object.keys(cfg.priceExpressions)
    .filter((k) => k !== "between")
    .sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const re = new RegExp(
      `${key.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\s+${amount}\\s*${WEIGHT_UNIT}\\b`,
      "i",
    );
    const m = text.match(re);
    if (!m) continue;
    const value = parseAmount(m[1] ?? "");
    if (value == null) continue;
    const kind = cfg.priceExpressions[key] === "min" ? "min" : "max";
    return {
      filter: { phrase: m[0], type: kind, ...(kind === "max" ? { max: value } : { min: value }) },
      text: text.replace(m[0], " "),
    };
  }

  // bare weight ("5000 lb travel trailer") → ±10% window
  const bare = text.match(new RegExp(`\\b${amount}\\s*${WEIGHT_UNIT}\\b`, "i"));
  if (bare) {
    const value = parseAmount(bare[1] ?? "");
    if (value != null && value >= 100) {
      return {
        filter: {
          phrase: bare[0],
          type: "exact",
          min: Math.round(value * 0.9),
          max: Math.round(value * 1.1),
        },
        text: text.replace(bare[0], " "),
      };
    }
  }
  return { filter: null, text };
}

function detectIntent(text: string, cfg: QueryLanguageConfig, removedPhrases: string[]): string {
  const haystack = `${text} ${removedPhrases.join(" ")}`;
  let best = "product_lookup";
  let bestScore = 0;
  for (const [intent, words] of Object.entries(cfg.intentWords)) {
    let score = 0;
    for (const w of words) if (` ${haystack} `.includes(` ${w} `)) score++;
    if (score > bestScore) {
      bestScore = score;
      best = intent;
    }
  }
  return bestScore === 0 ? "product_lookup" : best;
}

/** Merge measurement pairs so "50 amp" / "12 v" stay one protected identifier. */
function glueMeasurements(text: string, cfg: QueryLanguageConfig): string {
  const units = cfg.measurementExpressions.map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  return text.replace(new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s+(${units})\\b`, "gi"), "$1$2");
}

export function detectPhrases(text: string, cfg: QueryLanguageConfig): string[] {
  const found: string[] = [];
  const padded = ` ${text} `;
  const candidates = new Set<string>([
    ...cfg.knownPhrases,
    ...Object.keys(cfg.aliases).filter((a) => a.includes(" ")),
    ...Object.values(cfg.aliases).flat().filter((a) => a.includes(" ")),
  ]);
  for (const phrase of candidates) {
    if (padded.includes(` ${phrase.toLowerCase()} `)) found.push(phrase.toLowerCase());
  }
  return [...new Set(found)].sort((a, b) => b.length - a.length);
}

/** Canonical alias phrase for any variant ("5th wheel" -> "fifth wheel"). */
export function canonicalAlias(phrase: string, cfg: QueryLanguageConfig): string | null {
  const p = phrase.toLowerCase();
  for (const [canonical, variants] of Object.entries(cfg.aliases)) {
    if (canonical === p) return canonical;
    if (variants.some((v) => v.toLowerCase() === p)) return canonical;
  }
  return null;
}

export function parseQuery(
  rawQuery: string,
  cfg: QueryLanguageConfig = DEFAULT_QUERY_LANGUAGE,
): ParsedQuery {
  const originalQuery = rawQuery ?? "";
  let text = glueMeasurements(normalizeText(originalQuery), cfg);
  const normalizedQuery = text;

  // request phrases (multi-word) first, so "i need a" never pollutes terms
  const stripped = stripPhrases(text, cfg.requestPhrases);
  text = stripped.text;

  const intent = detectIntent(text, cfg, stripped.removed);

  const temporal = detectTemporal(text, cfg);
  text = temporal.text;

  // length before weight/price: "25 ft" is a size, not dollars or pounds
  const lengthed = detectLength(text, cfg);
  text = lengthed.text;

  // weight before price: "under 5000 lbs" is a weight limit, not a price cap
  const weighted = detectWeight(text, cfg);
  text = weighted.text;

  const priced = detectPrice(text, cfg);
  text = priced.text;

  const conditioned = detectCondition(text);
  text = conditioned.text;

  // body style ("travel trailer", "5th wheel") — a category, not a title token
  const rvTyped = detectRvType(text);
  text = rvTyped.text;

  // feature requests ("with solar", "bunkhouse") — removed from the term list
  const featured = detectFeatureRequests(text);
  text = featured.text;

  const phrases = detectPhrases(text, cfg);

  const rawTokens = text.split(/[\s,]+/).map((t) => t.replace(/^[.'-]+|[.'-]+$/g, "")).filter(Boolean);

  const filler = new Set(cfg.conversationFiller.map((w) => w.toLowerCase()));
  const stops = new Set(cfg.hardStopWords.map((w) => w.toLowerCase()));

  const identifiers: string[] = [];
  const years: number[] = [];
  const context: string[] = [];
  const terms: string[] = [];

  for (const token of rawTokens) {
    if (/^(19|20)\d{2}$/.test(token)) {
      years.push(Number(token));
      identifiers.push(token);
      terms.push(token);
      continue;
    }
    if (isProtectedToken(token, cfg)) {
      identifiers.push(token);
      terms.push(token);
      continue;
    }
    if (CONTEXT_WORDS.has(token)) {
      context.push(token);
      continue;
    }
    if (filler.has(token) || stops.has(token)) continue;
    if (token.length < 2) continue;
    terms.push(stemToken(token, cfg));
  }

  // alias/synonym expansion (extra, optional matches)
  const expanded = new Set<string>();
  for (const term of terms) {
    for (const [canonical, variants] of Object.entries(cfg.aliases)) {
      const all = [canonical, ...variants].map((v) => v.toLowerCase());
      if (all.includes(term)) {
        for (const v of all) v.split(" ").forEach((w) => expanded.add(stemToken(w, cfg)));
      }
    }
    for (const syn of cfg.synonyms[term] ?? []) {
      syn.split(" ").forEach((w) => expanded.add(stemToken(w, cfg)));
    }
  }
  for (const phrase of phrases) {
    const canonical = canonicalAlias(phrase, cfg);
    if (!canonical) continue;
    canonical.split(" ").forEach((w) => expanded.add(stemToken(w, cfg)));
  }
  terms.forEach((t) => expanded.delete(t));

  return {
    originalQuery,
    normalizedQuery,
    intent,
    searchTerms: [...new Set(terms)],
    expandedTerms: [...expanded].filter(Boolean),
    phrases,
    context: [...new Set(context)],
    identifiers: [...new Set(identifiers)],
    years: [...new Set(years)],
    temporalContext: temporal.ctx,
    priceFilter: priced.filter,
    weightFilter: weighted.filter,
    lengthFilter: lengthed.filter,
    conditionFilter: conditioned.filter,
    rvTypeFilter: rvTyped.id ? { phrase: rvTyped.phrase ?? rvTyped.id, id: rvTyped.id } : null,
    featureTags: featured.tags,
  };
}

/** Tokenizer shared with the compiler so index tokens match query terms. */
export function tokenizeForIndex(
  value: string,
  cfg: QueryLanguageConfig = DEFAULT_QUERY_LANGUAGE,
): string[] {
  const text = glueMeasurements(normalizeText(value ?? ""), cfg);
  const stops = new Set(cfg.hardStopWords.map((w) => w.toLowerCase()));
  const out: string[] = [];
  for (const token of text.split(/[\s,]+/)) {
    const t = token.replace(/^[.'-]+|[.'-]+$/g, "");
    if (!t || t.length < 2) continue;
    if (isProtectedToken(t, cfg)) {
      out.push(t);
      continue;
    }
    if (stops.has(t)) continue;
    out.push(stemToken(t, cfg));
  }
  return [...new Set(out)];
}
